import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { AppView, NavigationHeader } from 'design-system';
import Button from 'design-system/Button';
import MoneyInput from 'design-system/MoneyInput';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { rem } from 'design-system/values';
import AmountSelectors from 'design-system/AmountSelectors';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import Amplitude from 'utils/amplitude';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { OnboardingFlow } from 'utils/types/navigation';
import { INITIAL_INVESTMENT_AMOUNTS, MAXIMUM_INVEST_AMOUNT, MONTHLY_INVESTMENT_AMOUNTS } from '../constants';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { selectAutoinvestMinAmount } from '../reducers/selectors';
const strings = {
    navTitle: 'Choose amount',
    initialNavTitle: 'Choose initial deposit',
    button: 'Continue',
};
const SetupContributionsChooseAmountScreen = ({ route, navigation }) => {
    const dispatch = useAppDispatch();
    const { type } = route.params;
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const colors = useColors();
    const styles = useStyles(styleSet);
    const scrollY = useSharedValue(0);
    const navigateToNextScreen = useNavigateToNextScreen();
    const minAmount = useAppSelector(selectAutoinvestMinAmount);
    const moneyInputRef = useRef(null);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, navigation]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            if (type === 'initial') {
                Amplitude.logEvent('Choose Initial Deposit', { source: 'invest' });
            }
            else if (type === 'monthly') {
                Amplitude.logEvent('Choose Direct Debit Amount', { source: 'invest' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const amountRef = useRef(0);
    const handleContinue = useCallback(() => {
        if (type === 'initial') {
            dispatch({
                type: 'SAVE_ONBOARDING_STATE',
                amount: {
                    initial: amountRef.current,
                },
            });
        }
        else if (type === 'monthly') {
            if (amountRef.current < minAmount || amountRef.current > MAXIMUM_INVEST_AMOUNT) {
                moneyInputRef.current?.shake();
                return;
            }
            dispatch({
                type: 'SAVE_ONBOARDING_STATE',
                amount: {
                    monthly: amountRef.current,
                },
            });
        }
        navigateToNextScreen();
    }, [minAmount, navigateToNextScreen, type]);
    const onAmountChange = useCallback((value) => {
        amountRef.current = value;
    }, []);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.statusNegative,
    }), [colors.text.primary, colors.text.statusNegative]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    const numPadHeader = useMemo(() => (<View style={styles.numPadHeader}>
        <Spacer h={24}/>
        <AmountSelectors amounts={type === 'initial' ? INITIAL_INVESTMENT_AMOUNTS : MONTHLY_INVESTMENT_AMOUNTS} moneyInputRef={moneyInputRef}/>
      </View>), [styles.numPadHeader, type]);
    return (<AppView>
      <View style={mountedOnAnotherScreen ? [styles.container, styles.regularPaddingBottom] : paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        <NavigationHeader scrollY={scrollY} regularTitleHeight isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} title={type === 'initial' ? strings.initialNavTitle : strings.navTitle} rightContent={<View style={mountedOnAnotherScreen ? styles.headerRight : null}>
              <HelpScreenButton />
            </View>}/>
        <MoneyInput flex max={MAXIMUM_INVEST_AMOUNT} showMinHint showMaxHint ref={moneyInputRef} colors={moneyInputColors} restrictAmountsLessThanOne pinPadStyle={styles.pinPad} numPadHeader={numPadHeader} alwaysDisplayMinHint={false} alwaysDisplayMaxHint={false} onValueChange={onAmountChange} min={type === 'initial' ? undefined : minAmount} initialValue={route.params.initialAmount}/>
        <Spacer h={30}/>
        <View style={styles.buttonContainer}>
          <Button brand title={strings.button} onPress={handleContinue}/>
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    pinPad: {
        marginTop: rem(10),
    },
    numPadHeader: {
        flex: 2,
        width: '100%',
    },
    headerRight: {
        alignItems: 'flex-end',
        paddingRight: rem(16),
        marginBottom: rem(6),
    },
    regularPaddingBottom: {
        paddingBottom: rem(16),
    },
}));
export default SetupContributionsChooseAmountScreen;
